import React from "react";
import ReactMarkdown from "react-markdown";
import { type UIMessage } from "@ai-sdk/ui-utils";
import { ReasoningPart } from "./ReasoningPart";
import { ToolCallItem } from "./ToolCallItem";
import styles from "./Composer.module.css";

interface MessageItemProps {
  message: UIMessage;
  index: number;
  editingMessageId: string | null;
  editingMessageContent: string;
  editingTextareaRef: React.RefObject<HTMLTextAreaElement>;
  handleStartEditing: (id: string, content: string) => void;
  handleSubmitEditedMessage: (
    event:
      | React.KeyboardEvent<HTMLTextAreaElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => void;
  handleCancelEditing: () => void;
  handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  setEditingMessageContent: (content: string) => void;
  expandedArgs: Record<string, boolean>;
  toggleArgExpansion: (toolCallId: string) => void;
  collapsedReasoning: Record<string, boolean>;
  completedReasoning: Record<string, boolean>;
  toggleReasoningCollapse: (reasoningId: string) => void;
  collapsedToolResponses: Record<string, boolean>;
  toggleToolResponseCollapse: (toolCallId: string) => void;
}

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  index,
  editingMessageId,
  editingMessageContent,
  editingTextareaRef,
  handleStartEditing,
  handleSubmitEditedMessage,
  handleCancelEditing,
  handleInputChange,
  setEditingMessageContent,
  expandedArgs,
  toggleArgExpansion,
  collapsedReasoning,
  completedReasoning,
  toggleReasoningCollapse,
  collapsedToolResponses,
  toggleToolResponseCollapse,
}) => {
  const getMessageStyle = (role: string) => {
    switch (role) {
      case "user":
        return styles.userMessage;
      case "assistant":
        return styles.assistantMessage;
      case "system":
        return styles.systemMessage;
      case "error":
        return styles.errorMessage;
      default:
        return styles.assistantMessage;
    }
  };

  if (message.role === "user" && message.id === editingMessageId) {
    return (
      <div key={message.id || index} className={`${styles.editingUserMessage}`}>
        <div className={styles.inputContainer}>
          <textarea
            ref={editingTextareaRef}
            value={editingMessageContent}
            onChange={(e) => {
              setEditingMessageContent(e.target.value);
              handleInputChange(e);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmitEditedMessage(e);
              }
            }}
            className={styles.inputField}
            placeholder="Edit your message..."
          />
          <div className={styles.editButtons}>
            <button
              className={styles.sendButton}
              onClick={(e) => {
                e.preventDefault();
                handleSubmitEditedMessage(e);
              }}
            >
              edit
            </button>
            <button
              onClick={handleCancelEditing}
              className={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (message.parts && message.parts.length > 0) {
    return (
      <div
        key={message.id || index}
        className={`${styles.messageItem} ${getMessageStyle(message.role)}`}
        aria-hidden="true"
        onClick={() =>
          message.role === "user" &&
          handleStartEditing(message.id || `msg-${index}`, message.content)
        }
        style={{
          cursor: message.role === "user" ? "pointer" : "default",
        }}
      >
        {message.parts.map((part: any, partIndex: number) => {
          if (part.type === "text") {
            return (
              <div
                key={`text-${partIndex}`}
                className={styles.messageItemMarkdown}
              >
                <ReactMarkdown>{part.text}</ReactMarkdown>
              </div>
            );
          } else if (part.type === "reasoning" && part.reasoning) {
            const reasoningId = `${message.id}-${part.reasoning.substring(0, 20)}`;
            const isCompleted = !!completedReasoning[reasoningId];
            const isCollapsed = collapsedReasoning[reasoningId] || false;

            return (
              <ReasoningPart
                key={`reasoning-${partIndex}`}
                reasoning={part.reasoning}
                isCollapsed={isCollapsed}
                onToggleCollapse={() => toggleReasoningCollapse(reasoningId)}
                isCompleted={isCompleted}
              />
            );
          } else if (part.type === "tool-invocation" && part.toolInvocation) {
            return (
              <div
                key={`tool-${partIndex}`}
                className={styles.toolInvocationContainer}
              >
                <ToolCallItem
                  toolInvocation={part.toolInvocation}
                  onToggleArgs={toggleArgExpansion}
                  isArgsExpanded={
                    !!expandedArgs[part.toolInvocation.toolCallId]
                  }
                  isResponseCollapsed={
                    !!collapsedToolResponses[part.toolInvocation.toolCallId]
                  }
                  onToggleResponse={toggleToolResponseCollapse}
                />
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  }

  return (
    <div key={message.id || index}>
      <div
        aria-hidden="true"
        className={`${styles.messageItem} ${getMessageStyle(message.role)}`}
        onClick={() =>
          message.role === "user" &&
          handleStartEditing(message.id || `msg-${index}`, message.content)
        }
        style={{
          cursor: message.role === "user" ? "pointer" : "default",
        }}
      >
        <ReactMarkdown>{message.content}</ReactMarkdown>
      </div>

      {message.toolInvocations && message.toolInvocations.length > 0 && (
        <div className={styles.toolInvocationContainer}>
          {message.toolInvocations.map((toolInvocation: any) => (
            <ToolCallItem
              key={toolInvocation.toolCallId}
              toolInvocation={toolInvocation}
              onToggleArgs={toggleArgExpansion}
              isArgsExpanded={!!expandedArgs[toolInvocation.toolCallId]}
            />
          ))}
        </div>
      )}
    </div>
  );
};
